import React, { useState, useLayoutEffect } from "react";

/* -=-=- amcharts -=-=- */
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { VscFilePdf } from "react-icons/vsc";
import moment from "moment";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { PdfSurveyStartDate, PdfSurveyEndDate, EncodeImageDataUrl } from "../../common/DateFormat";

am4core.useTheme(am4themes_animated);
am4core.addLicense("CH236795548");

const QuestionGroupRatingChart = ({
  chartData,
  Key,
  Value,
  optionText,
  divId,
  surveyDetails,
  QuestionText,
  QuestionNo,
  QuestionDetails,
}) => {
  const charts = ["Column View", "Lollipop View", "Stack View", "Gauge View", "Pie View", "Radial View", "Curved View"];

  const [changeView, setChangeView] = useState("Column");

  var chart = React.useRef(null);
  useLayoutEffect(() => {
    setColumnViewChart(chartData, Key, Value, optionText, "columnGroupModel" + divId);
  }, [chartData, Key, Value, divId, surveyDetails, QuestionText, QuestionNo]);

  const setColumnViewChart = (chartData, Key, Value, optionText, divId) => {
    chart.current = am4core.create(divId, am4charts.XYChart);
    am4core.options.onlyShowOnViewport = true;
    chart.current.hiddenState.properties.opacity = 0; // this makes initial fade in effect

    chart.current.data = chartData;

    chart.current.padding(40, 40, 0, 0);
    chart.current.maskBullets = false; // allow bullets to go out of plot area

    var text = chart.current.plotContainer.createChild(am4core.Label);
    //   text.text = "Drag column bullet to change its value";
    text.y = 92;
    text.x = am4core.percent(100);
    text.horizontalCenter = "right";
    text.zIndex = 100;
    text.fillOpacity = 0.7;

    // category axis
    var categoryAxis = chart.current.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = Key;
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.minGridDistance = 2;
    categoryAxis.fontSize = "11";
    categoryAxis.fontWeight = "500";
    categoryAxis.renderer.labels.template.wrap = true;
    categoryAxis.renderer.labels.template.truncate = false;
    categoryAxis.renderer.labels.template.maxWidth = 150;

    categoryAxis.renderer.labels.template.horizontalCenter = "middle";
    categoryAxis.renderer.labels.template.verticalCenter = "middle";
    categoryAxis.renderer.labels.template.rotation = -90;
    categoryAxis.renderer.minHeight = 20;

    // value axis
    var valueAxis = chart.current.yAxes.push(new am4charts.ValueAxis());
    // we set fixed min/max and strictMinMax to true, as otherwise value axis will adjust min/max while dragging and it won't look smooth
    valueAxis.strictMinMax = true;
    valueAxis.min = 0;
    //   valueAxis.max = 100;
    valueAxis.renderer.minWidth = 60;
    valueAxis.fontSize = "11";
    valueAxis.fontWeight = "500";

    // series
    var series = chart.current.series.push(new am4charts.ColumnSeries());
    series.dataFields.categoryX = Key;
    series.dataFields.optionX = optionText;

    series.dataFields.valueY = Value;
    series.dataFields.keyY = "visits";

    series.tooltip.pointerOrientation = "vertical";
    series.tooltip.dy = -8;
    series.sequencedInterpolation = true;
    series.defaultState.interpolationDuration = 1500;
    series.columns.template.strokeOpacity = 0;
    series.tooltip.label.wrap = true;
    series.tooltip.maxWidth = 300;
    series.tooltip.background.fill = am4core.color("#333333");
    series.tooltip.background.stroke = "#ffffff";
    series.tooltip.getFillFromObject = false;
    series.tooltip.fontSize = "12";
    series.tooltip.label.fill = "#ffffff";
    series.tooltip.label.padding(15, 15, 15, 15);

    // label bullet
    var labelBullet = new am4charts.LabelBullet();
    series.bullets.push(labelBullet);
    labelBullet.label.text = "" + " {keyY}";
    labelBullet.label.fontSize = "13";
    labelBullet.label.fill = "#999";
    labelBullet.label.background.fill = "#ffffff";
    labelBullet.strokeOpacity = 0;
    labelBullet.stroke = am4core.color("#dadada");
    labelBullet.dy = -20;

    // series bullet
    var bullet = series.bullets.create();
    bullet.stroke = am4core.color("#ffffff");
    bullet.strokeWidth = 3;
    bullet.opacity = 1; // initially invisible
    bullet.defaultState.properties.opacity = 1;
    // resize cursor when over
    // bullet.cursorOverStyle = am4core.MouseCursorStyle.verticalResize;
    bullet.draggable = false;

    // create hover state
    var hoverState = bullet.states.create("hover");
    hoverState.properties.opacity = 1; // visible when hovered

    // add circle sprite to bullet
    var circle = bullet.createChild(am4core.Circle);
    circle.radius = 8;
    circle.fill = "#46bfd8";
    circle.propertyFields.fill = "white";

    // gradient
    var gradient = new am4core.LinearGradient();
    gradient.addColor(am4core.color("#46bfd8"));
    gradient.addColor(am4core.color("#5cdbf5"));
    gradient.rotation = 90;

    //on hover tooltip content
    var tooltipHTML = `
      <strong style="margin:0 0 5px; display:block;">{optionX}</strong>
      <p>{QuestionText}</p>
      <hr style="margin:15px 0"/>
      <div style="text-transform:uppercase;">
        Average Rating:<strong> {keyY}</strong>
      </div>
      `;

    // column template
    var columnTemplate = series.columns.template;
    columnTemplate.column.cornerRadiusTopLeft = 8;
    columnTemplate.column.cornerRadiusTopRight = 8;
    columnTemplate.column.fillOpacity = 0.8;
    // columnTemplate.tooltipText = "Question text will be displayed here.";
    //   columnTemplate.width = 70;
    // columnTemplate.column.fill = "#46bfd8";
    columnTemplate.column.fill = gradient;
    columnTemplate.tooltipY = 0; // otherwise will point to middle of the column
    columnTemplate.tooltipHTML = tooltipHTML;

    // hover state
    var columnHoverState = columnTemplate.column.states.create("hover");
    columnHoverState.properties.fillOpacity = 1;

    // you can change any property on hover state and it will be animated
    columnHoverState.properties.cornerRadiusTopLeft = 35;
    columnHoverState.properties.cornerRadiusTopRight = 35;

    // show bullet when hovered
    columnTemplate.events.on("over", (event) => {
      var dataItem = event.target.dataItem;
      var itemBullet = dataItem.bullets.getKey(bullet.uid);
      itemBullet.isHover = true;
    });

    // hide bullet when mouse is out
    columnTemplate.events.on("out", (event) => {
      var dataItem = event.target.dataItem;
      var itemBullet = dataItem.bullets.getKey(bullet.uid);
      itemBullet.isHover = false;
    });

    // as by default columns of the same series are of the same color, we add adapter which takes colors from chart.current.colors color set
    columnTemplate.adapter.add("fill", (fill, target) => {
      return chart.current.colors.getIndex(target.dataItem.index).saturate(0.3);
    });

    bullet.adapter.add("fill", (fill, target) => {
      return chart.current.colors.getIndex(target.dataItem.index).saturate(0.3);
    });
  };

  const saveQuestionPDF = () => {
    var tbl = [];
    for (var i = 0; i < chartData.length; i++) {
      if (i % 2 === 0) {
        tbl.push([
          { text: chartData[i].index, margin: [5, 5], alignment: "center" },
          {
            text: chartData[i].country,
            fontSize: 9,
            margin: [5, 5],
          },
          {
            text: chartData[i].responses,
            fontSize: 9,
            alignment: "right",
            margin: [5, 5],
          },
          {
            text: chartData[i].visits,
            fontSize: 9,
            alignment: "right",
            margin: [5, 5],
          },
        ]);
      } else {
        tbl.push([
          {
            text: chartData[i].index,
            fillColor: "#f5f5f5",
            fontSize: 9,
            margin: [5, 5],
            alignment: "center",
          },
          {
            text: chartData[i].country,
            fontSize: 9,
            fillColor: "#f5f5f5",
            margin: [5, 5],
          },
          {
            text: chartData[i].responses,
            fillColor: "#f5f5f5",
            fontSize: 9,
            alignment: "right",
            margin: [5, 5],
          },
          {
            text: chartData[i].visits,
            fillColor: "#f5f5f5",
            fontSize: 9,
            alignment: "right",
            margin: [5, 5],
          },
        ]);
      }
    }
    Promise.all([chart.current.exporting.pdfmake, chart.current.exporting.getImage("png")]).then(function (res) {
      var pdfMake = res[0];

      // Create document template
      var doc = {
        // userPassword: "123",
        // ownerPassword: "123456",
        // permissions: {
        //   printing: "highResolution", //'lowResolution'
        //   modifying: false,
        //   copying: false,
        //   annotating: true,
        //   fillingForms: true,
        //   contentAccessibility: true,
        //   documentAssembly: true,
        // },

        info: {
          title: surveyDetails.SurveyName,
        },
        pageSize: "A4",
        pageOrientation: "portrait",
        pageMargins: [30, 30, 30, 60],
        content: [],

        footer: function (currentPage, pageCount) {
          return {
            margin: [30, 30, 30, 0],
            columns: [
              {
                text: "Powered by",
                alignment: "left",
                color: "#3d3d3d",
                fontSize: 9,
              },
              {
                text: "OpenEyes Technologies Inc.",
                alignment: "left",
                color: "#00aef3",
                fontSize: 9,
                margin: [-129, 0, 0, 0],
                link: "https://theopeneyes.com/",
              },
              {
                text: currentPage.toString() + " of " + pageCount,
                color: "#3d3d3d",
                alignment: "right",
                fontSize: 9,
              },
              // {
              //   text: "Powered by OpenEyes Surveys",
              //   alignment: "left",
              //   color: "#3d3d3d",
              //   fontSize: 10,
              //   link: "https://theopeneyes.com/",
              // },
              // {
              //   text: currentPage.toString() + " of " + pageCount,
              //   color: "#3d3d3d",
              //   alignment: "right",
              //   fontSize: 10,
              //   // link: "https://theopeneyes.com/",
              // },
            ],
          };
        },
      };

      //survey name and logo
      let encodeImageData = EncodeImageDataUrl(surveyDetails);
      if (encodeImageData != "") {
        doc.content.push({
          margin: [0, 0, 0, 25],
          columns: [
            {
              image: EncodeImageDataUrl(surveyDetails),
              alignment: "left",
              maxWidth: 100,
            },
            {
              text: surveyDetails.SurveyName,
              alignment: "right",
              fontSize: 20,
            },
          ],
        });
      } else {
        doc.content.push({
          width: "100%",
          text: surveyDetails.SurveyName,
          alignment: "center",
          fontSize: 14,
          fontWeight: 600,
          margin: [0, 0, 0, 10],
        });
      }

      //dates
      doc.content.push({
        margin: [0, 0, 0, 0],
        alignment: "center",
        borderColor: "#000",
        backgroundColor: "#fff",
        columns: [
          {
            text: PdfSurveyStartDate(surveyDetails.StartDate),
            fontSize: 9,
          },
          {
            text: PdfSurveyEndDate(surveyDetails.ExpiryDate),
            fontSize: 9,
          },
          {
            text: "Downloaded On:\n" + moment().format("MM/DD/YYYY h:mm A"),
            fontSize: 9,
          },
          // {
          //   text: "Status",
          //   fontSize: 10,
          // },
        ],
        columnGap: 30,
      });

      //border jugad
      doc.content.push({
        alignment: "center",
        margin: [0, 20, 0, 0],
        columns: [
          {
            text: "Total Respondents: " + surveyDetails.uniqueUsers,
            color: "#ffffff",
            fontSize: 10,
            // characterSpacing: 3,
            decoration: "overline",
            decorationColor: "#3d3d3c",
            decorationStyle: "line",
          },
          {
            text: "Total Questions: " + surveyDetails.totalQuestions,
            color: "#ffffff",
            fontSize: 10,
            // characterSpacing: 3,
            decoration: "overline",
            decorationColor: "#3d3d3c",
            decorationStyle: "line",
          },
          {
            text: "Total Responses: " + surveyDetails.totalquestionResponses,
            color: "#ffffff",
            fontSize: 10,
            // characterSpacing: 3,
            decoration: "overline",
            decorationColor: "#3d3d3c",
            decorationStyle: "line",
          },
        ],
        columnGap: 20,
      });

      //survey question & responses
      doc.content.push({
        alignment: "center",
        margin: [0, 0, 0, 0],
        columns: [
          {
            text: "Total Respondents: " + surveyDetails.uniqueUsers,
            fontSize: 10,
          },
          {
            text: "Total Questions: " + surveyDetails.totalQuestions,
            fontSize: 10,
          },
          {
            text: "Total Responses: " + surveyDetails.totalquestionResponses,
            fontSize: 10,
          },
        ],
        columnGap: 20,
      });

      //border jugad
      doc.content.push({
        alignment: "center",
        columns: [
          {
            text: "Total Respondents: " + surveyDetails.uniqueUsers + "\n\n",
            color: "#ffffff",
            fontSize: 10,
            // characterSpacing: 3,
            decoration: "underline",
            decorationColor: "#3d3d3c",
            decorationStyle: "line",
          },
          {
            text: "Total Questions: " + surveyDetails.totalQuestions + "\n\n",
            color: "#ffffff",
            fontSize: 10,
            // characterSpacing: 3,
            decoration: "underline",
            decorationColor: "#3d3d3c",
            decorationStyle: "line",
          },
          {
            text: "Total Responses: " + surveyDetails.totalquestionResponses + "\n\n",
            color: "#ffffff",
            fontSize: 10,
            // characterSpacing: 3,
            decoration: "underline",
            decorationColor: "#3d3d3c",
            decorationStyle: "line",
          },
        ],
        columnGap: 20,
      });

      //survey message
      doc.content.push({
        text: surveyDetails.SurveyStatus === "Closed" ? "" : "This data is not final as the survey is ongoing",
        alignment: "right",
        fontSize: 9,
        // margin: [-25, -25, 0, 0],
        // background: surveyDetails.SurveyStatus === "Closed" ? "#dc3545" : "#81d888",
        color: "#dc3545",
      });

      //survey descriptipn
      doc.content.push({
        text: surveyDetails.FormattedText,
        fontSize: 11,
        alignment: "left",
        margin: [0, 10, 0, 0],
      });

      //line space
      doc.content.push({
        width: 250,
        text: "\n\n",
        alignment: "center",
      });

      //Question Text
      doc.content.push({
        width: 250,
        text: QuestionNo + ". " + QuestionText,
        alignment: "left",
        fontSize: 15,
        margin: [0, 0, 0, 10],
      });

      //line space
      doc.content.push({
        width: 250,
        text: "\n\n",
        alignment: "center",
      });

      //chart image
      doc.content.push({
        image: res[1],
        width: 530,
      });

      //line space
      doc.content.push({
        width: 250,
        text: "\n\n",
        alignment: "center",
      });

      // table;
      doc.content.push({
        // layout: "lightHorizontalLines",
        fontSize: 11,
        table: {
          headerRows: 1,
          // widths: ["*", "*", "*", "*"],
          widths: ["*", 250, 120, 75],
          body: [
            [
              {
                text: "#",
                fillColor: "#32c5ff",
                color: "white",
                alignment: "center",
                margin: [5, 5],
              },
              {
                text: "Option",
                fillColor: "#32c5ff",
                color: "white",
                alignment: "left",
                margin: [5, 5],
              },
              {
                text: "Number of Responses",
                fillColor: "#32c5ff",
                color: "white",
                alignment: "right",
                margin: [5, 5],
              },
              {
                text: "Average Rating",
                fillColor: "#32c5ff",
                color: "white",
                alignment: "right",
                margin: [5, 5],
              },
            ],
            ...tbl,
          ],
        },
      });

      // pdfMake.createPdf(doc).open();
      pdfMake
        .createPdf(doc)
        .download(
          surveyDetails.SurveyName != ""
            ? surveyDetails.SurveyName.split(" ").join("").replace(/\s+/g, "-") +
            "-" +
            "Q-#" +
            QuestionNo +
            "-" +
            moment().format("MM-DD-YYYY-hh-mm") +
            ".pdf"
            : "Survey-" + "Q-#" + QuestionNo + "-" + moment().format("MM-DD-YYYY-hh-mm") + ".pdf"
        );
    });
  };

  return (
    <>
      <div
        className="survey-chart-select"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          margin: "15px 0 0 0",
        }}>
        {" "}
        <div className="individual-pdf">
          <span onClick={() => saveQuestionPDF()} title="Download PDF" className="save-pdf btn btn-primary">
            <VscFilePdf />
          </span>
        </div>
      </div>

      <div class="graph_scroll_head mt-4">Scroll sideways to view complete graph</div>

      <div className="graph-respo QuestionGroupRatingChart">
        <div
          id={"columnGroupModel" + divId}
          className={"chart-column-main " + `${changeView == "Column" ? "" : "d-none"}`}
          style={{}}></div>
      </div>
    </>
  );
};

export default QuestionGroupRatingChart;
